<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import ColorPicker from 'dashboard/components/pipelines/ColorPicker.vue';

export default {
  components: {
    NextButton,
    ColorPicker,
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      name: '',
      color: '#64748B',
      requiresJustification: false,
    };
  },
  validations: {
    name: {
      required,
      minLength: minLength(2),
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'dealLossReasons/getUIFlags',
      accountId: 'getCurrentAccountId',
    }),
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async create() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      try {
        await this.$store.dispatch('dealLossReasons/create', {
          accountId: this.accountId,
          lossReason: {
            name: this.name.trim(),
            color: this.color,
            requires_justification: this.requiresJustification,
          },
        });
        useAlert(this.$t('DEAL_LOSS_REASONS.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.errors?.[0] ||
          this.$t('DEAL_LOSS_REASONS.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('DEAL_LOSS_REASONS.ADD.TITLE')"
      :header-content="$t('DEAL_LOSS_REASONS.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="create">
      <woot-input
        v-model="name"
        class="w-full"
        :class="{ error: v$.name.$error }"
        :label="$t('DEAL_LOSS_REASONS.FORM.NAME.LABEL')"
        :placeholder="$t('DEAL_LOSS_REASONS.FORM.NAME.PLACEHOLDER')"
        :error="v$.name.$error ? $t('DEAL_LOSS_REASONS.FORM.NAME.ERROR') : ''"
      />

      <div class="w-full mb-4">
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('DEAL_LOSS_REASONS.FORM.COLOR.LABEL') }}
        </label>
        <ColorPicker v-model="color" />
      </div>

      <div class="flex items-center w-full gap-2 mb-4">
        <input v-model="requiresJustification" type="checkbox" :value="true" />
        <label class="text-sm text-n-slate-12">
          {{ $t('DEAL_LOSS_REASONS.FORM.REQUIRES_JUSTIFICATION.LABEL') }}
        </label>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('DEAL_LOSS_REASONS.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('DEAL_LOSS_REASONS.FORM.CREATE')"
          :disabled="uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
